import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const authApi = {
  wsToken: () => http.get("/auth/ws-token").then((r) => r.data.token),
};

export const photosApi = {
  list: (jobId) => http.get(`/jobs/${jobId}/photos`).then((r) => r.data),
  upload: (jobId, file, kind = "other", note = "") => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    if (note) form.append("note", note);
    return http
      .post(`/jobs/${jobId}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  remove: (photoId) => http.delete(`/photos/${photoId}`).then((r) => r.data),
  url: (photoId) => `${API}/photos/${photoId}`,
};

export const fleetApi = {
  list: () => http.get("/assets").then((r) => r.data),
  get: (id) => http.get(`/assets/${id}`).then((r) => r.data),
  create: (data) => http.post("/assets", data).then((r) => r.data),
  update: (id, data) => http.patch(`/assets/${id}`, data).then((r) => r.data),
  remove: (id) => http.delete(`/assets/${id}`).then((r) => r.data),
  tick: () => http.post("/assets/simulate-tick").then((r) => r.data),
  trail: (id, hours = 1) => http.get(`/assets/${id}/trail`, { params: { hours } }).then((r) => r.data),
};

export const jobsApi = {
  list: () => http.get("/jobs").then((r) => r.data),
  create: (data) => http.post("/jobs", data).then((r) => r.data),
  update: (id, data) => http.patch(`/jobs/${id}`, data).then((r) => r.data),
  remove: (id) => http.delete(`/jobs/${id}`).then((r) => r.data),
  pdfUrl: (id) => `${API}/jobs/${id}/pdf`,
  uploadPhoto: (jobId, formData) =>
    http.post(`/jobs/${jobId}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
};

export const zonesApi = {
  list: () => http.get("/zones").then((r) => r.data),
  create: (data) => http.post("/zones", data).then((r) => r.data),
  remove: (id) => http.delete(`/zones/${id}`).then((r) => r.data),
};

export const alertsApi = {
  list: (params = {}) => http.get("/alerts", { params }).then((r) => r.data),
  ack: (id) => http.post(`/alerts/${id}/ack`).then((r) => r.data),
};

export const complianceApi = {
  calculate: (data) => http.post("/compliance/calculate", data).then((r) => r.data),
  bridgeFormula: (data) => http.post("/compliance/bridge-formula", data).then((r) => r.data),
};

export const companyApi = {
  me: () => http.get("/company/me").then((r) => r.data),
  updateSettings: (data) => http.patch("/company/me/settings", data).then((r) => r.data),
};

export const userPrefsApi = {
  getNotificationPrefs: () => http.get("/me/notification-prefs").then((r) => r.data),
  updateNotificationPrefs: (data) => http.patch("/me/notification-prefs", data).then((r) => r.data),
};

export const axleTemplatesApi = {
  list: () => http.get("/axle-templates").then((r) => r.data),
  create: (data) => http.post("/axle-templates", data).then((r) => r.data),
  remove: (id) => http.delete(`/axle-templates/${id}`).then((r) => r.data),
};

export const jobBridgeApi = {
  check: (jobId) => http.post(`/jobs/${jobId}/bridge-check`).then((r) => r.data),
};

export const superApi = {
  listCompanies: () => http.get("/super/companies").then((r) => r.data),
  createCompany: (data) => http.post("/super/companies", data).then((r) => r.data),
  deleteCompany: (id, force = false) =>
    http.delete(`/super/companies/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),
  listInvites: (companyId) =>
    http.get("/super/invites", { params: companyId ? { company_id: companyId } : {} }).then((r) => r.data),
  createInvite: (data) => http.post("/super/invites", data).then((r) => r.data),
  revokeInvite: (id) => http.delete(`/super/invites/${id}`).then((r) => r.data),
  viewCompany: (company_id) => http.post("/super/me/view-company", { company_id }).then((r) => r.data),
};

export const inviteApi = {
  preview: (token) => http.post("/invites/preview", { token }).then((r) => r.data),
  accept: (data) => http.post("/invites/accept", data).then((r) => r.data),
};

export const billingApi = {
  plans: () => http.get("/payments/plans").then((r) => r.data),
  status: () => http.get("/payments/billing-status").then((r) => r.data),
  startCheckout: (plan_id) =>
    http.post("/payments/checkout", { plan_id, origin_url: window.location.origin }).then((r) => r.data),
  checkoutStatus: (session_id) =>
    http.get(`/payments/checkout/${session_id}`).then((r) => r.data),
};

export const shiftApi = {
  get: () => http.get("/me/shift").then((r) => r.data),
  start: (asset_id) => http.post("/me/shift/start", { asset_id }).then((r) => r.data),
  end: () => http.post("/me/shift/end").then((r) => r.data),
};

export const dashboardApi = {
  kpis: () => http.get("/dashboard/kpis").then((r) => r.data),
};

export const reportsApi = {
  dailyPdfUrl: (date, sections = null) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (sections) {
      Object.entries(sections).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v));
      });
    }
    const qs = params.toString();
    return `${API}/reports/daily/pdf${qs ? `?${qs}` : ""}`;
  },
  sendDailyTest: () => http.post("/reports/daily/email-test").then((r) => r.data),
};

export const integrationsApi = {
  samsaraStatus: () => http.get("/integrations/samsara/status").then((r) => r.data),
  samsaraConnect: (data) => http.post("/integrations/samsara/connect", data).then((r) => r.data),
  samsaraSync: () => http.post("/integrations/samsara/sync").then((r) => r.data),
  samsaraDisconnect: () => http.delete("/integrations/samsara").then((r) => r.data),
  peoplenetStatus: () => http.get("/integrations/peoplenet/status").then((r) => r.data),
  peoplenetConnect: (data) => http.post("/integrations/peoplenet/connect", data).then((r) => r.data),
  peoplenetSync: () => http.post("/integrations/peoplenet/sync").then((r) => r.data),
  peoplenetDisconnect: () => http.delete("/integrations/peoplenet").then((r) => r.data),
  listDevices: () => http.get("/integrations/devices").then((r) => r.data),
  provisionDevice: (data) => http.post("/integrations/devices", data).then((r) => r.data),
  revokeDevice: (id) => http.delete(`/integrations/devices/${id}`).then((r) => r.data),
};

export const routesApi = {
  geocode: (address, country) => http.post("/routes/geocode", { address, country }).then((r) => r.data),
  optimize: (data) => http.post("/routes/optimize", data).then((r) => r.data),
  list: (params = {}) => http.get("/routes", { params }).then((r) => r.data),
  get: (id) => http.get(`/routes/${id}`).then((r) => r.data),
  create: (data) => http.post("/routes", data).then((r) => r.data),
  update: (id, data) => http.patch(`/routes/${id}`, data).then((r) => r.data),
  delete: (id) => http.delete(`/routes/${id}`).then((r) => r.data),
  myToday: () => http.get("/me/route/today").then((r) => r.data),
  completeStop: (routeId, seq, proofPhotoId = null, gps = null) => {
    const body = {};
    if (proofPhotoId) body.proof_photo_id = proofPhotoId;
    if (gps && gps.lat != null && gps.lng != null) {
      body.lat = gps.lat;
      body.lng = gps.lng;
      if (gps.accuracy_m != null) body.accuracy_m = gps.accuracy_m;
    }
    return http.post(`/routes/${routeId}/stops/${seq}/complete`, body).then((r) => r.data);
  },
};

export const maintenanceApi = {
  due: () => http.get("/maintenance/due").then((r) => r.data),
  summary: (days = 365) => http.get("/maintenance/summary", { params: { days } }).then((r) => r.data),
  list: (assetId) => http.get(`/assets/${assetId}/maintenance`).then((r) => r.data),
  log: (assetId, data) => http.post(`/assets/${assetId}/maintenance`, data).then((r) => r.data),
};

export const auditApi = {
  list: (params = {}) => http.get("/super/audit", { params }).then((r) => r.data),
  exportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/super/audit/export.csv${qs ? `?${qs}` : ""}`;
  },
  purge: (before_days) => http.delete("/super/audit/purge", { params: { before_days } }).then((r) => r.data),
};
